import React, { useState } from 'react';
import { useAsync } from 'react-use';
import { UserStatsApi } from '../API/UserStatsApi';
import { Period } from '../Types/Period';
import { formatPercentage } from '../Utils/Number';
import { StatCard } from './StatCard';

export const StatsSection: React.FC = () => {
  const [period, setPeriod] = useState<Period | null>(Period.THIS_WEEK);
  const [pastPeriod, setPastPeriod] = useState<Period | null>(Period.PAST_WEEK);

  const userStatsApi = new UserStatsApi();

  const { loading: isStatsLoading, value: stats } = useAsync(async () => {
    return await userStatsApi.getUserStats(period, pastPeriod);
  }, [period]);

  const isLoading = isStatsLoading || !stats;

  const calculateChange = (current: number, previous?: number) => {
    if (!previous) return 0;
    return ((current - previous) / previous) * 100;
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-neutral-800">Stats</h2>
        <select
          value={period ?? ''}
          onChange={(e) => {
            setPeriod(e.target.value ? (e.target.value as Period) : null);
            setPastPeriod(
              (e.target.getAttribute('data-previous') as Period) ?? null,
            );
          }}
          className="bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm"
        >
          <option value={Period.THIS_WEEK} data-previous={Period.PAST_WEEK}>
            This Week
          </option>
          <option value={Period.THIS_MONTH} data-previous={Period.PAST_MONTH}>
            This Month
          </option>
          <option value="">All Time</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 flex-1">
        <StatCard
          title="Accuracy"
          loading={isLoading}
          value={formatPercentage(stats?.current.accuracy)}
          change={calculateChange(
            stats?.current.accuracy,
            stats?.previous?.accuracy,
          )}
          icon={
            <img src={require('../assets/icons/target.svg')} alt="Accuracy" />
          }
        />
        <StatCard
          title="Reading Time"
          loading={isLoading}
          value={`${Math.floor(stats?.current.totalArticleTime / (1000 * 60 * 60))}h ${Math.floor((stats?.current.totalArticleTime % (1000 * 60 * 60)) / (1000 * 60))}m`}
          change={calculateChange(
            stats?.current.totalArticleTime,
            stats?.previous?.totalArticleTime,
          )}
          icon={
            <img
              src={require('../assets/icons/clock.svg')}
              alt="Reading Time"
            />
          }
        />
        <StatCard
          title="Words/Min"
          loading={isLoading}
          value={Math.round(
            stats?.current.averageWordsPerMinuteRead,
          )?.toString()}
          change={calculateChange(
            stats?.current.averageWordsPerMinuteRead,
            stats?.previous?.averageWordsPerMinuteRead,
          )}
          icon={
            <img
              src={require('../assets/icons/eyes.svg')}
              alt="Words per Minute"
            />
          }
        />
        <StatCard
          title="Challenging Words"
          loading={isLoading}
          value={stats?.current.challengingWords.toString()}
          change={calculateChange(
            stats?.current.challengingWords,
            stats?.previous?.challengingWords,
          )}
          icon={
            <img
              src={require('../assets/icons/sheet.svg')}
              alt="Challenging Words"
            />
          }
        />
      </div>
    </div>
  );
};
